import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import MfaService from '../../../api/services/MfaService';
import PatientService from '../../../api/services/PatientService';
import { RootState } from '../../../store';

type MfaState = {
  isLoading: boolean;
  message: string;
  mfaCodeSent: boolean;
  mfaVerified: boolean;
  mfaPreference?: string;
  smsConsent?: boolean;
  isSmsConsentLoading: boolean;
  error?: string;
};

const initialState: MfaState = {
  isLoading: false,
  message: '',
  mfaCodeSent: false,
  mfaVerified: false,
  mfaPreference: undefined,
  smsConsent: undefined,
  isSmsConsentLoading: false,
  error: undefined,
};

const ACTIONS_PREFIX = 'mfa';

export const generateMfaCodeThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/generateMfaCode`,
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await MfaService.generateMfaCode(email);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const verifyMfaCodeThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/verifyMfaCode`,
  async ({ email, code }: { email: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await MfaService.verifyMfaCode(email, code);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getMfaPreferenceThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getMfaPreference`,
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await MfaService.getMfaPreference(email);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateMfaPreferenceThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/updateMfaPreference`,
  async ({ email, mfaType }: { email: string; mfaType: 'email' | 'sms' }, { rejectWithValue }) => {
    try {
      const response = await MfaService.updateMfaPreference(email, mfaType);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getSmsConsentThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getSmsConsent`,
  async (patientId: string, { rejectWithValue }) => {
    try {
      const response = await PatientService.getSmsConsent(patientId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateSmsConsentThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/updateSmsConsent`,
  async (
    { patientId, smsConsent }: { patientId: string; smsConsent: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await PatientService.updateSmsConsent(patientId, smsConsent);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const mfaSlice = createSlice({
  name: 'mfa',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Generate MFA Code
      .addCase(generateMfaCodeThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(generateMfaCodeThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.mfaCodeSent = true;
      })
      .addCase(generateMfaCodeThunk.rejected, (state) => {
        state.isLoading = false;
      })

      // Verify MFA Code
      .addCase(verifyMfaCodeThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(verifyMfaCodeThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.mfaVerified = true;
      })
      .addCase(verifyMfaCodeThunk.rejected, (state) => {
        state.isLoading = false;
      })

      // Get MFA Preference
      .addCase(getMfaPreferenceThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(getMfaPreferenceThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message;
        state.mfaPreference = action.payload.resource;
      })
      .addCase(getMfaPreferenceThunk.rejected, (state) => {
        state.isLoading = false;
      })

      // Update MFA Preference
      .addCase(updateMfaPreferenceThunk.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(updateMfaPreferenceThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.message = action.payload.message;
      })
      .addCase(updateMfaPreferenceThunk.rejected, (state) => {
        state.isLoading = false;
      })

      // Get SMS Consent
      .addCase(getSmsConsentThunk.pending, (state) => {
        state.isSmsConsentLoading = true;
        state.error = undefined;
      })
      .addCase(getSmsConsentThunk.fulfilled, (state, action) => {
        state.isSmsConsentLoading = false;
        state.message = action.payload.message;
        state.smsConsent = action.payload.resource?.sms_consent;
      })
      .addCase(getSmsConsentThunk.rejected, (state) => {
        state.isSmsConsentLoading = false;
      })

      // Update SMS Consent
      .addCase(updateSmsConsentThunk.pending, (state) => {
        state.isSmsConsentLoading = true;
        state.error = undefined;
      })
      .addCase(updateSmsConsentThunk.fulfilled, (state, action) => {
        state.isSmsConsentLoading = false;
        state.message = action.payload.message;
        state.smsConsent = action.payload.resource?.sms_consent;
      })
      .addCase(updateSmsConsentThunk.rejected, (state) => {
        state.isSmsConsentLoading = false;
      });
  },
});

// Selectors
export const selectMfaState = (state: RootState) => state.mfa;

// Reducer
export default mfaSlice.reducer;
